import {
  LandingPage,
  SearchableVerticalList,
  SearchableVerticalListDataType,
  tyrioIcons,
} from '@tyrio/ui-library';

import api from '@tyrio/api-factory';
import { DBClientApi } from '@tyrio/dto';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { pageSize } from '../../../../constants/pagination.constants';
import { useDebounce } from '../../hooks/useDebounce';
import { ExpandableLayout } from '../../layouts/ExpandableLayout';
import routeNames from '../../lib/routeNames';

interface DashboardClientProps {
  children: React.ReactNode;
}

const DashboardClient = ({ children }: DashboardClientProps) => {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);
  const location = useLocation();

  const { isFetching, isError, data } = useQuery(
    ['client_list', debouncedSearch, page],
    () => searchClients(search, page),
    {}
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const [selected, setSelected] = useState<Record<string, boolean>>(
    location.pathname.split('/').pop() !== 'clients'
      ? { [location.pathname.split('/').pop()]: true }
      : {}
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (type: string, item?: any) => {
    Object.keys(selected).forEach((key) => {
      selected[key] = false;
    });
    if (type === 'itemClicked') {
      setSelected((prevValue) => ({
        ...prevValue,
        [item.id]: !prevValue[item.id],
      }));
      history.push(`/dashboard/clients/${item.id}`);
    }
    if (type === 'new') {
      history.push(`/dashboard/clients/new`);
    }
  };

  const searchClients = async (search: string, page: number) => {
    return await api.fetch<DBClientApi['list']>('get_clients', {
      search: search,
      page,
      pageSize: pageSize,
    });
  };

  return (
    <ExpandableLayout
      sidebarComponent={
        <SearchableVerticalList
          header="Clients"
          type={SearchableVerticalListDataType.WITHOUT_CHILDREN}
          data={
            data?.data?.map((item) => ({
              id: item.id,
              name: item.shortName,
              type: item.clientType,
              active: item.active,
              disabled: !item.active,
              isSelected: selected[item.id],
            })) || []
          }
          path={routeNames.dashboard()}
          isFetching={isFetching}
          isError={isError}
          onSearchChange={setSearch}
          onCreateNew={() => handleClick('new', {})}
          handleItemClick={(item) => handleClick('itemClicked', item)}
          count={data?.count}
          onChangePage={(page) => {
            setPage(page);
          }}
          pageNumber={page}
        />
      }
      landingPage={
        <LandingPage
          icon={tyrioIcons.clientIcon}
          title={'Welcome to Client settings'}
          subtitle={'Choose client from the list or create new.'}
          buttonText={'Add new client'}
          cyId={'create-new-client-landing'}
          onClick={() => handleClick('new', {})}
        />
      }
    >
      {children}
    </ExpandableLayout>
  );
};

export default DashboardClient;
